import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Download, FileText } from "lucide-react";
import { api, API } from "@/lib/api";
import { ClaimStatusBadge } from "@/components/shared";

export const CollectionStoryHubPage = () => {
  const [stories, setStories] = useState([]);
  
  useEffect(() => {
    api.get("/collection-story").then(res => setStories(res.data)).catch(() => {});
  }, []);
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="collection-story-hub">
      <h1 className="text-3xl font-bold serif-heading mb-4">The Ticket Collection Story</h1>
      <p className="text-gray-600 mb-8 leading-relaxed">
        Institutional custody narrative documenting the origin, acquisition, and stewardship of this collection.
        Written as historical documentation, not memoir.
      </p>
      
      <div className="space-y-6">
        {stories.map((story) => (
          <div key={story.id} className="archive-card p-6">
            <h3 className="text-lg font-semibold serif-heading mb-3">{story.title}</h3>
            <p className="text-gray-600 leading-relaxed">{story.narrative}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const GovernancePage = () => (
  <div className="max-w-4xl mx-auto px-4 py-12" data-testid="governance-page">
    <h1 className="text-3xl font-bold serif-heading mb-8">Governance & Editorial Policy</h1>
    
    <div className="space-y-8">
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Mission Statement</h2>
        <p className="text-gray-600 leading-relaxed">
          This archive exists to create the definitive, evidence-driven historical record of Elvis Presley&apos;s 
          Aloha from Hawaii 1973 concert, focused on tickets, production artifacts, provenance, and factual 
          correction of long-repeated inaccuracies.
        </p>
      </section>
      
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Editorial Control</h2>
        <ul className="space-y-3 text-gray-600">
          <li className="flex items-start gap-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <span>Editorial control is centralized. No public artifact submission.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <span>No voting, community edits, or public comments.</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
            <span>All sources visible. Corrections logged publicly.</span>
          </li>
        </ul>
      </section>
      
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Claim Classification</h2>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <ClaimStatusBadge status="Documented" />
            <p className="text-sm text-gray-600">Supported by primary source documentation</p>
          </div>
          <div className="flex items-start gap-4">
            <ClaimStatusBadge status="Disputed" />
            <p className="text-sm text-gray-600">Evidence contradicts or questions the claim</p>
          </div>
          <div className="flex items-start gap-4">
            <ClaimStatusBadge status="Unverified" />
            <p className="text-sm text-gray-600">Insufficient documentation to verify</p>
          </div>
        </div>
      </section>
      
      {/* Core Evidence Pack Download */}
      <section className="archive-card p-6 bg-blue-50 border-blue-200">
        <h2 className="text-xl font-semibold serif-heading mb-4 flex items-center gap-2">
          <Download className="w-5 h-5" />
          Core Evidence Pack
        </h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          Download the complete Core Evidence Pack containing all Documented claims with their full 
          evidence chains, supporting citations, and linked fact corrections. Suitable for institutional 
          deposit and scholarly citation.
        </p>
        <a
          href={`${API}/pdf/archive/core-evidence`}
          download
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
          data-testid="download-core-evidence-btn"
        >
          <FileText className="w-4 h-4" />
          Download Core Evidence Pack (PDF)
        </a>
      </section>
      
      {/* Archive Versioning Policy */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Archive Versioning Policy</h2>
        <div className="space-y-4 text-gray-600">
          <p className="leading-relaxed">
            The archive generates versioned PDF snapshots for institutional deposit and scholarly citation. 
            Each snapshot is cryptographically hashed for integrity verification.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Snapshot Schedule</h3>
            <p className="text-sm">
              Archive packs are generated on-demand and scheduled for periodic refresh. Snapshots are only 
              created when underlying data has changed, ensuring each version represents meaningful updates.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Immutability Guarantee</h3>
            <p className="text-sm">
              Once generated, archive packs are never modified or deleted. Each version is preserved 
              indefinitely with its original content hash and generation timestamp.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">How to Cite Archive Packs</h3>
            <p className="text-sm mb-2">When citing archive packs, include:</p>
            <code className="block bg-white p-3 rounded border text-xs">
              Elvis Aloha 1973 Historical Archive. "[Pack Name]." Version [N]. Generated [Date]. 
              File Hash: [hash]. https://elvisaloha1973.com/archives
            </code>
          </div>
        </div>
        <div className="mt-4">
          <Link 
            to="/archives" 
            className="text-blue-600 hover:text-blue-800 font-medium text-sm inline-flex items-center gap-1"
          >
            View Archive Downloads →
          </Link>
        </div>
      </section>
    </div>
  </div>
);

/* ============================================================================
   BB-067 BATCH 1: METHODOLOGY PAGE
   
   Source Files:
   - /app/research/aloha-history-source-hierarchy.md
   - /app/research/source-reliability-rubric.md
   
   Purpose: Provide source-backed methodology documentation
   ============================================================================ */
export const MethodologyPage = () => (
  <div className="max-w-4xl mx-auto px-4 py-12" data-testid="methodology-page">
    <div className="mb-8">
      <p className="text-xs font-semibold tracking-widest uppercase text-amber-700 mb-2">Research Standards</p>
      <h1 className="text-3xl font-bold serif-heading mb-4">Methodology & Source Hierarchy</h1>
      <p className="text-gray-600 leading-relaxed">
        This archive applies strict evidentiary standards to every historical claim. Modern institutional 
        sources cannot automatically be treated as conclusive—contemporaneous documentation from 1972-1973 
        takes precedence over later promotional materials, regardless of their official status.
      </p>
    </div>
    
    <div className="space-y-8">
      {/* Core Principle */}
      <section className="archive-card p-6 bg-amber-50 border-amber-200">
        <h2 className="text-xl font-semibold serif-heading mb-4">Core Principle</h2>
        <p className="text-gray-700 leading-relaxed font-medium">
          Sony Legacy, Graceland, and government publications may repeat large audience claims, but 
          contemporaneous ratings, schedules, and transmission records are required to verify audience 
          size, country totals, and "worldwide live" language.
        </p>
      </section>
      
      {/* Source Hierarchy */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-6">Source Hierarchy</h2>
        
        <div className="space-y-6">
          {/* Level 1 */}
          <div className="border-l-4 border-green-500 pl-4">
            <h3 className="font-semibold text-gray-900 mb-2">Level 1: Contemporaneous Primary Evidence</h3>
            <p className="text-sm text-gray-600 mb-3">
              Sources created at or near the time of the event (1972-1973) by parties directly involved or observing.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Hawaii newspapers:</strong> Honolulu Star-Bulletin, Honolulu Advertiser</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Wire services:</strong> Associated Press, United Press International</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Broadcast documentation:</strong> NBC schedules, NHK records, network affiliate logs</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Technical records:</strong> RCA production specs, COMSAT transmission logs</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Financial records:</strong> Kui Lee Cancer Fund disbursement documentation</span>
              </div>
            </div>
          </div>
          
          {/* Level 2 */}
          <div className="border-l-4 border-blue-500 pl-4">
            <h3 className="font-semibold text-gray-900 mb-2">Level 2: Physical Artifact Evidence</h3>
            <p className="text-sm text-gray-600 mb-3">
              Objects created as part of the production or event, subject to physical examination.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Original tickets:</strong> Fifty admission tickets documented in the collection; complete provenance and custody history remain under review</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Ticket proofs:</strong> Approval proofs with inscriptions (verification pending)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700"><strong>Printing layouts:</strong> Production artwork with documented attribution</span>
              </div>
            </div>
          </div>
          
          {/* Level 3 */}
          <div className="border-l-4 border-yellow-500 pl-4">
            <h3 className="font-semibold text-gray-900 mb-2">Level 3: Verified Secondary Sources</h3>
            <p className="text-sm text-gray-600 mb-3">
              Later works that cite primary sources and can be cross-referenced.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700">Scholarly works with documented research methodology</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700">Trade publications with cited measurement sources</span>
              </div>
            </div>
          </div>
          
          {/* Level 4 */}
          <div className="border-l-4 border-orange-500 pl-4">
            <h3 className="font-semibold text-gray-900 mb-2">Level 4: Institutional Statements</h3>
            <p className="text-sm text-gray-600 mb-3">
              Official statements from rights holders that require independent verification.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700">Sony Legacy liner notes and promotional materials</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700">Graceland exhibition text and visitor materials</span>
              </div>
            </div>
          </div>
          
          {/* Level 5 */}
          <div className="border-l-4 border-red-500 pl-4">
            <h3 className="font-semibold text-gray-900 mb-2">Level 5: Repeated Claims</h3>
            <p className="text-sm text-gray-600 mb-3">
              Widely circulated statements without traceable documentation—treated with skepticism.
            </p>
            <div className="grid gap-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700">"Over one billion viewers" claims without ratings documentation</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-1.5 flex-shrink-0"></span>
                <span className="text-gray-700">Country-count claims without transmission records</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Claim Classification */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Claim Classification System</h2>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <ClaimStatusBadge status="Documented" />
            <div>
              <p className="text-sm text-gray-900 font-medium">Documented</p>
              <p className="text-sm text-gray-600">Supported by Level 1-2 primary source documentation</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <ClaimStatusBadge status="Disputed" />
            <div>
              <p className="text-sm text-gray-900 font-medium">Disputed</p>
              <p className="text-sm text-gray-600">Evidence contradicts or questions the claim</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <ClaimStatusBadge status="Unverified" />
            <div>
              <p className="text-sm text-gray-900 font-medium">Unverified</p>
              <p className="text-sm text-gray-600">Insufficient contemporaneous documentation to verify</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Owner Verification */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Owner Verification Requirements</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          Certain claims require direct verification from the collection owner before they can be 
          stated as fact. These include physical measurements, inscription transcriptions, and 
          provenance details that cannot be confirmed from photographs alone.
        </p>
        <div className="bg-gray-50 p-4 rounded-lg text-sm">
          <p className="text-gray-700">
            Items marked <strong>[OWNER VERIFICATION PENDING]</strong> represent claims where the 
            archive has evidence suggesting a particular answer, but final confirmation requires 
            the owner&apos;s direct testimony or physical examination.
          </p>
        </div>
      </section>
      
      {/* Open Questions */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Transparent Limitations</h2>
        <p className="text-gray-600 leading-relaxed mb-4">
          This archive acknowledges what it does not know. Open research questions are documented 
          explicitly rather than filled with speculation or inherited claims.
        </p>
        <div className="grid gap-3">
          <div className="flex items-start gap-3 text-sm">
            <span className="text-amber-600">●</span>
            <span className="text-gray-700">Some ticket proof inscriptions await owner verification</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="text-amber-600">●</span>
            <span className="text-gray-700">Complete broadcast territory list requires transmission records not yet located</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="text-amber-600">●</span>
            <span className="text-gray-700">Certain provenance chains have gaps that are documented rather than assumed</span>
          </div>
        </div>
      </section>
      
      {/* Sources and Supporting Research */}
      <section className="archive-card p-6 bg-gray-50">
        <h2 className="text-xl font-semibold serif-heading mb-4">Sources & Supporting Research</h2>
        <p className="text-sm text-gray-600 mb-4">
          This methodology is derived from the following research documentation maintained in the archive&apos;s scholarly layer:
        </p>
        <div className="grid gap-3">
          <div className="flex items-start gap-3 text-sm">
            <span className="text-gray-400">→</span>
            <div>
              <span className="text-gray-700 font-medium">Source Hierarchy Framework</span>
              <p className="text-xs text-gray-500">Defines the five-level evidence classification system</p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="text-gray-400">→</span>
            <div>
              <span className="text-gray-700 font-medium">Source Reliability Rubric</span>
              <p className="text-xs text-gray-500">Criteria for evaluating documentary and artifact evidence</p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="text-gray-400">→</span>
            <div>
              <span className="text-gray-700 font-medium">Broadcast Documentation Standards</span>
              <p className="text-xs text-gray-500">Requirements for verifying transmission and audience claims</p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="text-gray-400">→</span>
            <div>
              <span className="text-gray-700 font-medium">Audience Claims Audit Methodology</span>
              <p className="text-xs text-gray-500">Framework for evaluating repeated viewership estimates</p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="text-gray-400">→</span>
            <div>
              <span className="text-gray-700 font-medium">Artifact Examination Standards</span>
              <p className="text-xs text-gray-500">Protocols for physical documentation and owner verification</p>
            </div>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="text-gray-400">→</span>
            <div>
              <span className="text-gray-700 font-medium">Open Research Questions Register</span>
              <p className="text-xs text-gray-500">Documented gaps and unresolved inquiries</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4 italic">
          These documents classify and discuss sources; they do not independently prove the claims they evaluate.
        </p>
      </section>
      
      {/* Navigation */}
      <div className="flex flex-wrap gap-4 pt-4">
        <Link 
          to="/governance" 
          className="text-blue-600 hover:text-blue-800 font-medium text-sm inline-flex items-center gap-1"
        >
          ← Governance & Editorial Policy
        </Link>
        <Link 
          to="/facts" 
          className="text-blue-600 hover:text-blue-800 font-medium text-sm inline-flex items-center gap-1"
        >
          Facts & Evidence →
        </Link>
      </div>
    </div>
    
    {/* Independent Archive Disclaimer */}
    <div className="mt-12 pt-6 border-t border-gray-200">
      <p className="text-xs text-gray-500 leading-relaxed">
        <strong>Independent Historical Archive:</strong> This methodology documentation describes the 
        evidentiary standards applied by an independent archive. It is not affiliated with Elvis Presley 
        Enterprises, Graceland, or Sony Music Entertainment.
      </p>
    </div>
  </div>
);

export const CorrectionsLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedDiff, setExpandedDiff] = useState({});
  const location = useLocation();
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const itemId = params.get("item_id");
    const itemType = params.get("item_type");
    
    let url = "/corrections-log?limit=100";
    if (itemId) url += `&item_id=${itemId}`;
    if (itemType) url += `&item_type=${itemType}`;
    if (filterType && !itemType) url += `&item_type=${filterType}`;
    if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
    
    api.get(url).then(res => {
      setLogs(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [location.search, filterType, searchTerm]);
  
  const getItemLink = (log) => {
    const typeRoutes = {
      claim: `/claims/${log.item_id}`,
      artifact: `/artifacts/${log.item_slug || log.item_id}`,
      research: `/research/${log.item_slug || log.item_id}`,
      fact_correction: `/facts/${log.item_slug || log.item_id}`,
      ticket: `/tickets`,
      timeline: `/event/timeline`,
    };
    return typeRoutes[log.item_type] || null;
  };
  
  const getActionBadgeClass = (action) => {
    const classes = {
      create: "bg-green-100 text-green-700 border-green-200",
      update: "bg-blue-100 text-blue-700 border-blue-200",
      delete: "bg-red-100 text-red-700 border-red-200",
    };
    return classes[action] || "bg-gray-100 text-gray-700 border-gray-200";
  };
  
  const renderDiff = (diff) => {
    if (!diff || Object.keys(diff).length === 0) return null;
    return (
      <div className="mt-3 bg-gray-50 p-3 rounded text-xs font-mono">
        {Object.entries(diff).map(([key, value]) => (
          <div key={key} className="mb-2">
            <span className="font-semibold text-gray-600">{key}:</span>
            {value.old_count !== undefined ? (
              <span className="ml-2">
                {value.old_count} → {value.new_count}
                {value.added?.length > 0 && <span className="text-green-600"> +{value.added.length}</span>}
                {value.removed?.length > 0 && <span className="text-red-600"> -{value.removed.length}</span>}
              </span>
            ) : (
              <div className="ml-4">
                <div className="text-red-600 line-through">{String(value.old || "(empty)")}</div>
                <div className="text-green-600">{String(value.new || "(empty)")}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  
  const itemTypes = ["claim", "citation", "timeline", "ticket", "artifact", "research", "fact_correction", "media"];
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="corrections-log-page">
      <h1 className="text-3xl font-bold serif-heading mb-4">Corrections Log</h1>
      <p className="text-gray-600 mb-6">
        Public record of all corrections, updates, and revisions made to archive content.
        This log provides transparency and credibility for the archive's editorial process.
      </p>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border rounded text-sm"
          data-testid="filter-type-select"
        >
          <option value="">All Types</option>
          {itemTypes.map(type => (
            <option key={type} value={type}>{type.replace("_", " ")}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-2 border rounded text-sm w-48"
          data-testid="search-input"
        />
      </div>
      
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {logs.map((log) => {
            const itemLink = getItemLink(log);
            const hasDiff = log.diff && Object.keys(log.diff).length > 0;
            
            return (
              <div key={log.id} className="archive-card p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${getActionBadgeClass(log.action)}`}>
                        {log.action || "update"}
                      </span>
                      <Badge variant="outline" className="text-xs">{log.item_type?.replace("_", " ")}</Badge>
                      <span className="text-sm text-gray-500">
                        {new Date(log.date).toLocaleString()}
                      </span>
                    </div>
                    
                    {log.item_title && (
                      <p className="font-medium mb-1">{log.item_title}</p>
                    )}
                    <p className="text-sm text-gray-600">{log.change_summary}</p>
                    
                    {log.reason && (
                      <p className="text-sm text-gray-500 mt-1 italic">Reason: {log.reason}</p>
                    )}
                    
                    <div className="flex items-center gap-3 mt-3">
                      {itemLink && (
                        <Link 
                          to={itemLink}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View Item →
                        </Link>
                      )}
                      {hasDiff && (
                        <button
                          onClick={() => setExpandedDiff(prev => ({ ...prev, [log.id]: !prev[log.id] }))}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          {expandedDiff[log.id] ? "Hide Changes" : "Show Changes"}
                        </button>
                      )}
                    </div>
                    
                    {expandedDiff[log.id] && renderDiff(log.diff)}
                  </div>
                  <span className="text-xs text-gray-400 ml-4">{log.editor_name}</span>
                </div>
              </div>
            );
          })}
          
          {logs.length === 0 && (
            <p className="text-center text-gray-500 py-12">No corrections logged yet</p>
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================================================
   BB-067 BATCH 2: RIGHTS AND REPRODUCTIONS PAGE
   
   Route: /rights
   Navigation: ABOUT
   ============================================================================ */
export const RightsPage = () => (
  <div className="max-w-3xl mx-auto px-4 py-12" data-testid="rights-page">
    <div className="mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-amber-700 mb-2">Legal & Permissions</p>
      <h1 className="text-3xl font-bold serif-heading mb-4">Rights & Reproductions</h1>
      <p className="text-gray-600 leading-relaxed">
        This page describes the rights status of materials presented in the Elvis Aloha Archive 
        and how reproduction requests are handled.
      </p>
    </div>
    
    <div className="space-y-8">
      {/* Independent Archive Status */}
      <section className="archive-card p-6 bg-amber-50 border-amber-200">
        <h2 className="text-xl font-semibold serif-heading mb-4">Independent Archive Status</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Elvis Aloha Archive is an <strong>independent historical and educational archive</strong>. 
          It is <strong>not affiliated with, endorsed by, sponsored by, or officially connected with</strong>:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-amber-600">●</span>
            <span>Elvis Presley Enterprises</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600">●</span>
            <span>Graceland</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600">●</span>
            <span>Sony Music Entertainment</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600">●</span>
            <span>Any official Elvis Presley organization</span>
          </li>
        </ul>
      </section>
      
      {/* Archive Ownership vs Underlying Rights */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Archive Ownership vs. Underlying Rights</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          The archive may own or maintain:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm mb-4">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Original physical objects in the collection</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Collection photography created by the archive owner</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Original research, catalog descriptions, and site presentation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Digitization files created from collection materials, subject to underlying rights</span>
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed mb-4">
          This does <strong>not</strong> necessarily transfer ownership of:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Copyright in historical photographs</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Elvis Presley trademarks or publicity rights</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Recordings or broadcast footage</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Newspaper content or promotional artwork</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Third-party trademarks</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Materials owned by estates, publishers, broadcasters, photographers, or record companies</span>
          </li>
        </ul>
      </section>
      
      {/* Historical and Educational Presentation */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Historical & Educational Presentation</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Materials are presented for:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Historical documentation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Education and research</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Commentary and criticism</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Preservation and cataloging</span>
          </li>
        </ul>
        <div className="bg-gray-50 p-4 rounded mt-4 text-sm">
          <p className="text-gray-600 italic">
            Whether a particular use qualifies as fair use or another legal exception depends on 
            the specific material, purpose, jurisdiction, and circumstances.
          </p>
        </div>
      </section>
      
      {/* Rights Status Classifications */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Rights Status Classifications</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          The archive uses careful classifications for materials:
        </p>
        <div className="space-y-3">
          <div className="flex items-start gap-3 text-sm">
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Identified</span>
            <span className="text-gray-700">Rights holder identified</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">Unidentified</span>
            <span className="text-gray-700">Rights holder not yet identified</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">Unresolved</span>
            <span className="text-gray-700">Rights status has not been conclusively established</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">Archive-Created</span>
            <span className="text-gray-700">Archive-created photograph or content</span>
          </div>
          <div className="flex items-start gap-3 text-sm">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">Documentary</span>
            <span className="text-gray-700">Reproduced for historical and documentary context</span>
          </div>
        </div>
      </section>
      
      {/* Reproduction and Licensing */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Reproduction & Licensing Requests</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          The archive <strong>cannot grant rights it does not own</strong>. It may provide permission 
          only for archive-owned materials, such as:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm mb-4">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Original archive photography</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Original written research</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Original catalog text</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Original site graphics, where applicable</span>
          </li>
        </ul>
        <p className="text-gray-700 leading-relaxed">
          For third-party materials, requesters must obtain authorization from the applicable rights holder.
        </p>
      </section>
      
      {/* Credits, Corrections, and Takedowns */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Credits, Corrections & Takedown Requests</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          The archive maintains a process for:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Missing or incorrect credit</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Rights-holder identification</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Factual correction</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Privacy concern</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Removal or restricted-access request</span>
          </li>
        </ul>
        <p className="text-gray-600 text-sm mt-4">
          Requests will be evaluated in accordance with applicable law.
        </p>
      </section>
      
      {/* Prohibited Claims */}
      <section className="archive-card p-6 bg-red-50 border-red-200">
        <h2 className="text-xl font-semibold serif-heading mb-4">What This Archive Does Not Claim</h2>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Official licensing or Graceland/EPE authorization</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Ownership of Elvis Presley trademarks</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>Authority to license Sony, NBC, RCA, newspaper, or photographer-owned content</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>That all materials are public domain</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>That all archival publication is automatically fair use</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-red-500">✗</span>
            <span>That provenance is complete where it remains unresolved</span>
          </li>
        </ul>
      </section>
      
      {/* Navigation */}
      <div className="flex flex-wrap gap-4 pt-4">
        <Link to="/governance" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
          ← Governance & Editorial Policy
        </Link>
        <Link to="/accessibility" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
          Accessibility →
        </Link>
      </div>
    </div>
    
    {/* Page Disclaimer */}
    <div className="mt-12 pt-6 border-t border-gray-200">
      <p className="text-xs text-gray-500 leading-relaxed">
        This rights and reproductions policy describes the archive&apos;s practices and is not legal advice. 
        Consult appropriate legal counsel for specific rights questions.
      </p>
    </div>
  </div>
);

/* ============================================================================
   BB-067 BATCH 2: ACCESSIBILITY PAGE
   
   Route: /accessibility
   Navigation: ABOUT
   ============================================================================ */
export const AccessibilityPage = () => (
  <div className="max-w-3xl mx-auto px-4 py-12" data-testid="accessibility-page">
    <div className="mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-amber-700 mb-2">Accessibility</p>
      <h1 className="text-3xl font-bold serif-heading mb-4">Accessibility Statement</h1>
      <p className="text-gray-600 leading-relaxed">
        Elvis Aloha Archive is committed to making historical and educational material usable 
        by as many visitors as reasonably possible.
      </p>
    </div>
    
    <div className="space-y-8">
      {/* Commitment */}
      <section className="archive-card p-6 bg-teal-50 border-teal-200">
        <h2 className="text-xl font-semibold serif-heading mb-4">Accessibility Commitment</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Elvis Aloha Archive is <strong>working toward WCAG 2.2 Level AA</strong> and documents 
          known accessibility limitations as they are identified.
        </p>
        <p className="text-gray-600 text-sm italic">
          Note: Full WCAG conformance has not been independently audited. This statement describes 
          implemented accessibility features and known issues.
        </p>
      </section>
      
      {/* Current Accessibility Features */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Current Accessibility Features</h2>
        
        <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">Keyboard Navigation</h3>
        <ul className="space-y-2 text-gray-700 text-sm mb-4">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Keyboard-operable navigation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Visible focus indicators (3-pixel teal outline)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Mobile menu keyboard behavior</span>
          </li>
        </ul>
        
        <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">Structure & Semantics</h3>
        <ul className="space-y-2 text-gray-700 text-sm mb-4">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Semantic headings and landmarks</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Single H1 per page with logical hierarchy</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Accessible current-page indicators (<code className="bg-gray-100 px-1 rounded text-xs">aria-current</code>)</span>
          </li>
        </ul>
        
        <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">Images & Content</h3>
        <ul className="space-y-2 text-gray-700 text-sm mb-4">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Alternative text for meaningful images</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Decorative-image handling (<code className="bg-gray-100 px-1 rounded text-xs">aria-hidden</code>)</span>
          </li>
        </ul>
        
        <h3 className="text-base font-semibold text-gray-800 mt-4 mb-2">Responsive & Motion</h3>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Responsive layout from 320px to desktop</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Minimum touch-target sizing (44×44 pixels)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Reduced-motion support (<code className="bg-gray-100 px-1 rounded text-xs">prefers-reduced-motion</code>)</span>
          </li>
        </ul>
      </section>
      
      {/* Historical Documents */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Images & Historical Documents</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Archival materials may include:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm mb-4">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">●</span>
            <span>Handwriting and faded text</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">●</span>
            <span>Complex layouts and scans</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">●</span>
            <span>Low-contrast originals</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">●</span>
            <span>Images containing text</span>
          </li>
        </ul>
        <p className="text-gray-600 text-sm">
          Descriptions, captions, and summaries are provided where available. Full transcription 
          coverage has not been verified for all materials.
        </p>
      </section>
      
      {/* Keyboard and Zoom */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Keyboard & Zoom Support</h2>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Full keyboard navigation support</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Browser zoom up to 200% without horizontal scrolling</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Responsive reflow for smaller viewports</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Focus visibility maintained across interactions</span>
          </li>
        </ul>
      </section>
      
      {/* Mobile and Tablet */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Mobile & Tablet Access</h2>
        <p className="text-gray-700 leading-relaxed">
          The archive is designed to work across devices with responsive layouts, 
          touch-friendly controls, and mobile-safe typography.
        </p>
      </section>
      
      {/* Known Limitations */}
      <section className="archive-card p-6 bg-amber-50 border-amber-200">
        <h2 className="text-xl font-semibold serif-heading mb-4">Known Limitations</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          The following accessibility limitations have been identified:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-3">
            <span className="text-amber-600">●</span>
            <span>Historical scans with poor contrast</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-600">●</span>
            <span>Complex evidence tables may require horizontal scrolling on narrow viewports</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-600">●</span>
            <span>PDF research documents may need further remediation</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-600">●</span>
            <span>Incomplete transcripts for some archival images</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-600">●</span>
            <span>Image descriptions pending review</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="text-amber-600">●</span>
            <span>Third-party embedded content outside the archive&apos;s control</span>
          </li>
        </ul>
      </section>
      
      {/* Reporting */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Reporting Accessibility Problems</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          If you encounter accessibility barriers while using the Elvis Aloha Archive, please 
          let us know so we can address the issue. When reporting an accessibility problem, 
          please include:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>The page or artifact URL where you encountered the issue</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>A description of the accessibility barrier</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>The assistive technology used (if applicable)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Your preferred accessible format, when relevant</span>
          </li>
        </ul>
      </section>
      
      {/* Last Review */}
      <section className="archive-card p-6 bg-gray-50">
        <h2 className="text-lg font-semibold serif-heading mb-2">Review Date</h2>
        <p className="text-gray-600 text-sm">
          This accessibility statement was last reviewed on <strong>2026-07-05</strong>.
        </p>
      </section>
      
      {/* Navigation */}
      <div className="flex flex-wrap gap-4 pt-4">
        <Link to="/rights" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
          ← Rights & Reproductions
        </Link>
        <Link to="/privacy" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
          Privacy & Legal →
        </Link>
      </div>
    </div>
  </div>
);

/* ============================================================================
   BB-067 BATCH 2: PRIVACY AND LEGAL PAGE
   
   Route: /privacy
   Navigation: ABOUT
   
   Technical Audit Findings (2026-07-05):
   - PostHog Analytics: Session recording, person_profiles: "identified_only"
   - Google Fonts: fonts.googleapis.com, fonts.gstatic.com
   - localStorage: Admin authentication only (not visitor-facing)
   - No public user accounts, no contact forms, no email collection
   ============================================================================ */
export const PrivacyPage = () => (
  <div className="max-w-3xl mx-auto px-4 py-12" data-testid="privacy-page">
    <div className="mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-amber-700 mb-2">Privacy & Legal</p>
      <h1 className="text-3xl font-bold serif-heading mb-4">Privacy Policy</h1>
      <p className="text-gray-600 leading-relaxed">
        This page describes how Elvis Aloha Archive collects, uses, and protects information.
      </p>
    </div>
    
    <div className="space-y-8">
      {/* Information Collected */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Information Collected</h2>
        
        <h3 className="text-base font-semibold text-gray-800 mb-2">Automatically Collected</h3>
        <ul className="space-y-2 text-gray-700 text-sm mb-4">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">●</span>
            <span><strong>Server logs:</strong> Standard web server logs including IP addresses, 
            browser type, device information, referring pages, requested URLs, and timestamps</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">●</span>
            <span><strong>Analytics data:</strong> Page views, session duration, and general 
            interaction patterns (see Analytics section below)</span>
          </li>
        </ul>
        
        <h3 className="text-base font-semibold text-gray-800 mb-2">Not Collected from Visitors</h3>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✗</span>
            <span>Public user accounts or visitor authentication</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✗</span>
            <span>Email addresses (no newsletter, contact forms, or email collection)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✗</span>
            <span>Payment information</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✗</span>
            <span>File uploads from visitors</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✗</span>
            <span>Search terms (search queries are not retained)</span>
          </li>
        </ul>
      </section>
      
      {/* Cookies and Browser Storage */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Cookies & Browser Storage</h2>
        
        <h3 className="text-base font-semibold text-gray-800 mb-2">Analytics Cookies</h3>
        <p className="text-gray-700 leading-relaxed text-sm mb-4">
          The archive uses PostHog analytics, which sets cookies to understand how visitors 
          use the site. These cookies help distinguish unique visitors and track session activity.
        </p>
        
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2 border-b font-medium">Purpose</th>
                <th className="text-left p-2 border-b font-medium">Type</th>
                <th className="text-left p-2 border-b font-medium">Classification</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border-b">Session identification</td>
                <td className="p-2 border-b">Cookie</td>
                <td className="p-2 border-b">Analytics</td>
              </tr>
              <tr>
                <td className="p-2 border-b">Visitor distinction</td>
                <td className="p-2 border-b">Cookie</td>
                <td className="p-2 border-b">Analytics</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <h3 className="text-base font-semibold text-gray-800 mb-2">Local Storage</h3>
        <p className="text-gray-700 leading-relaxed text-sm">
          Browser local storage is used only for administrative functions (not visitor-facing). 
          Public visitors do not have data stored in local storage.
        </p>
      </section>
      
      {/* Analytics and Third Parties */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Analytics & Third-Party Services</h2>
        
        <h3 className="text-base font-semibold text-gray-800 mb-2">PostHog Analytics</h3>
        <p className="text-gray-700 leading-relaxed text-sm mb-4">
          The archive uses <strong>PostHog</strong> (us.i.posthog.com) for analytics. PostHog may 
          collect session recordings and interaction data to help understand how visitors use the site. 
          Person profiles are created only for identified users (administrative access).
        </p>
        
        <h3 className="text-base font-semibold text-gray-800 mb-2">Google Fonts</h3>
        <p className="text-gray-700 leading-relaxed text-sm mb-4">
          The archive loads fonts from <strong>Google Fonts</strong> (fonts.googleapis.com, fonts.gstatic.com). 
          When you visit the site, your browser makes requests to Google&apos;s servers to retrieve font files. 
          Google&apos;s privacy policy applies to this data.
        </p>
        
        <h3 className="text-base font-semibold text-gray-800 mb-2">Hosting Infrastructure</h3>
        <p className="text-gray-700 leading-relaxed text-sm">
          The archive is hosted on cloud infrastructure that processes standard server requests. 
          Hosting providers have their own privacy policies governing data they may collect.
        </p>
      </section>
      
      {/* Purpose of Processing */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Purpose of Processing</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Information is collected for:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Operating and maintaining the website</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Security and abuse prevention</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Understanding site usage patterns to improve reliability</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">→</span>
            <span>Identifying and fixing technical issues</span>
          </li>
        </ul>
      </section>
      
      {/* Data Retention */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Retention</h2>
        <p className="text-gray-700 leading-relaxed">
          Retention periods vary according to operational, security, archival, and legal requirements. 
          Specific periods remain under review where they have not yet been formally established.
        </p>
        <p className="text-gray-600 text-sm mt-3 italic">
          Server logs are retained for a limited period for security and troubleshooting. 
          Analytics data retention is governed by PostHog&apos;s data retention policies.
        </p>
      </section>
      
      {/* Security */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Security</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          Reasonable safeguards are used to protect information:
        </p>
        <ul className="space-y-2 text-gray-700 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>HTTPS encryption for all connections</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Regular security updates</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-teal-600">✓</span>
            <span>Access controls for administrative functions</span>
          </li>
        </ul>
        <p className="text-gray-600 text-sm mt-3">
          No internet service can guarantee absolute security. The archive cannot guarantee 
          complete security of transmitted data.
        </p>
      </section>
      
      {/* External Links */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">External Links</h2>
        <p className="text-gray-700 leading-relaxed">
          The archive may link to external websites for citations, sources, or additional information. 
          These external sites have their own privacy policies. The archive is not responsible for 
          the privacy practices of external websites.
        </p>
      </section>
      
      {/* User Requests */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">User Requests</h2>
        <p className="text-gray-700 leading-relaxed">
          Because the archive does not collect personal account information from visitors, most 
          data subject requests (access, correction, deletion) are not applicable to visitor data. 
          For specific privacy concerns, users may contact the archive through available channels.
        </p>
        <p className="text-gray-600 text-sm mt-3 italic">
          Note: Available rights and procedures depend on your jurisdiction.
        </p>
      </section>
      
      {/* Children */}
      <section className="archive-card p-6">
        <h2 className="text-xl font-semibold serif-heading mb-4">Children</h2>
        <p className="text-gray-700 leading-relaxed">
          The archive&apos;s content is educational and historical in nature, intended for general audiences 
          interested in music history and archival research.
        </p>
      </section>
      
      {/* Independent Archive Status */}
      <section className="archive-card p-6 bg-amber-50 border-amber-200">
        <h2 className="text-xl font-semibold serif-heading mb-4">Independent Archive Status</h2>
        <p className="text-gray-700 leading-relaxed">
          Elvis Aloha Archive is an independent historical archive. It is not affiliated with 
          Elvis Presley Enterprises, Graceland, Sony Music Entertainment, or any official 
          Elvis Presley organization. This privacy policy applies only to this archive.
        </p>
      </section>
      
      {/* Effective Date */}
      <section className="archive-card p-6 bg-gray-50">
        <h2 className="text-lg font-semibold serif-heading mb-2">Effective & Revision Dates</h2>
        <p className="text-gray-600 text-sm">
          <strong>Effective date:</strong> 2026-07-05<br />
          <strong>Last revised:</strong> 2026-07-05
        </p>
        <p className="text-gray-500 text-xs mt-2">
          Changes will be posted on this page with an updated revision date.
        </p>
      </section>
      
      {/* Legal Review Disclaimer */}
      <div className="bg-gray-100 p-4 rounded text-sm text-gray-600">
        <p>
          <strong>Note:</strong> This privacy policy describes the archive&apos;s practices to the 
          best of its knowledge. It has not been reviewed by legal counsel and should not be 
          relied upon as legal advice.
        </p>
      </div>
      
      {/* Navigation */}
      <div className="flex flex-wrap gap-4 pt-4">
        <Link to="/accessibility" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
          ← Accessibility
        </Link>
        <Link to="/governance" className="text-blue-600 hover:text-blue-800 font-medium text-sm">
          Governance & Editorial Policy →
        </Link>
      </div>
    </div>
  </div>
);

export default CollectionStoryHubPage;
